import { useEffect, useState } from "react";

import { PessoaAcessoResumoDTO } from "@/types/pessoa-acesso.type";
import { Skeleton } from "@/components/ui/skeleton";

const RADIUS = 54;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/** Anel de progresso animado (dispara no mount, respeita `prefers-reduced-motion` via CSS). */
function AdocaoGauge({ pct }: { pct: number }) {
  const [offset, setOffset] = useState(CIRCUMFERENCE);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setOffset(CIRCUMFERENCE * (1 - pct / 100)));
    return () => cancelAnimationFrame(raf);
  }, [pct]);

  return (
    <div className="relative h-32 w-32 shrink-0">
      <svg width="128" height="128" viewBox="0 0 128 128" className="-rotate-90">
        <circle cx="64" cy="64" r={RADIUS} fill="none" strokeWidth="9" className="stroke-muted" />
        <circle
          cx="64"
          cy="64"
          r={RADIUS}
          fill="none"
          strokeWidth="9"
          strokeLinecap="round"
          stroke="hsl(var(--primary))"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={offset}
          className="transition-[stroke-dashoffset] duration-1000 ease-out motion-reduce:transition-none"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
        <span className="font-mono text-[28px] font-bold leading-none text-foreground">{pct}%</span>
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          Hoje
        </span>
      </div>
    </div>
  );
}

function Readout({
  label,
  value,
  sub,
  live,
}: {
  label: string;
  value: React.ReactNode;
  sub: string;
  live?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5 border-t border-border/60 pt-3 first:border-t-0 first:pt-0 sm:border-l sm:border-t-0 sm:pl-5 sm:pt-0 sm:first:border-l-0 sm:first:pl-0">
      <span className="flex items-center gap-1.5 font-mono text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground">
        {live && (
          <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.2)] motion-reduce:animate-none" />
        )}
        {label}
      </span>
      <span className="font-mono text-2xl font-bold leading-none text-foreground">{value}</span>
      <span className="text-xs text-muted-foreground">{sub}</span>
    </div>
  );
}

export function AdocaoHero({
  resumo,
  emFerias,
  loading,
}: {
  resumo: PessoaAcessoResumoDTO;
  emFerias: number | undefined;
  loading: boolean;
}) {
  if (loading) return <Skeleton className="h-44 w-full rounded-xl" />;

  const acessaramHojePct =
    resumo.totalAtivos > 0 ? Math.round((resumo.acessaramHoje / resumo.totalAtivos) * 100) : 0;

  return (
    <div className="relative overflow-hidden rounded-xl border border-primary/20 bg-gradient-to-br from-primary/10 via-card to-card p-6 shadow-sm">
      <div className="mb-5">
        <p className="text-sm font-semibold">Sua equipe hoje</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Participação das pessoas ativas no Workflow.
        </p>
      </div>
      <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center">
        <AdocaoGauge pct={acessaramHojePct} />

        <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-5">
          <Readout
            label="Acessaram hoje"
            live
            value={
              <>
                {resumo.acessaramHoje}
                <span className="ml-1 text-sm font-medium text-muted-foreground">
                  / {resumo.totalAtivos}
                </span>
              </>
            }
            sub="de quem está ativo"
          />
          <Readout
            label="Equipe ativa"
            value={resumo.totalAtivos}
            sub="pessoas com cadastro ativo"
          />
          <Readout
            label="Em férias"
            value={emFerias ?? "—"}
            sub={emFerias === undefined ? "Resumo indisponível" : "ausências informadas"}
          />
        </div>
      </div>
    </div>
  );
}
